/**
 * Module-level draft store for sticker creation.
 *
 * Lives OUTSIDE React so the selected file/preview/fields survive any
 * remount of the creator UI (mobile keyboard, native picker round-trip,
 * focus loss, viewport resize, etc.).
 *
 * Subscribers re-render via a tiny pub-sub.
 */

export interface StickerDraft {
  file: File | null;
  objectUrl: string | null;
  packName: string;
  tags: string;
  isPublic: boolean;
  saveQueued: boolean;
  saveError: string | null;
  activeRequestId: string | null;
  audioBlob: Blob | null;
  audioUrl: string | null;
  audioDurationMs: number;
  audioTranscript: string;
}

const INITIAL: StickerDraft = {
  file: null,
  objectUrl: null,
  packName: "My Pack",
  tags: "",
  isPublic: false,
  saveQueued: false,
  saveError: null,
  activeRequestId: null,
  audioBlob: null,
  audioUrl: null,
  audioDurationMs: 0,
  audioTranscript: "",
};

let state: StickerDraft = { ...INITIAL };
const listeners = new Set<() => void>();

const TEXT_KEY = "sticker_create_draft_text_v2";

// Hydrate light text fields from sessionStorage on module load.
try {
  if (typeof window !== "undefined") {
    const raw = window.sessionStorage.getItem(TEXT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StickerDraft>;
      state = {
        ...state,
        packName: parsed.packName ?? state.packName,
        tags: parsed.tags ?? state.tags,
        isPublic: !!parsed.isPublic,
      };
    }
  }
} catch {
  /* ignore */
}

function persistText() {
  try {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(
      TEXT_KEY,
      JSON.stringify({
        packName: state.packName,
        tags: state.tags,
        isPublic: state.isPublic,
      }),
    );
  } catch {
    /* ignore */
  }
}

function emit() {
  listeners.forEach((l) => {
    try { l(); } catch { /* ignore */ }
  });
}

export function getStickerDraft(): StickerDraft {
  return state;
}

export function subscribeStickerDraft(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function setStickerFile(file: File | null) {
  // Revoke previous object URL if we own it.
  if (state.objectUrl && state.objectUrl.startsWith("blob:")) {
    try { URL.revokeObjectURL(state.objectUrl); } catch { /* ignore */ }
  }
  const url = file ? URL.createObjectURL(file) : null;
  state = {
    ...state,
    file,
    objectUrl: url,
    saveQueued: false,
    saveError: null,
    activeRequestId: null,
  };
  emit();
}

export function setStickerAudio(blob: Blob | null, durationMs = 0, transcript = "") {
  if (state.audioUrl && state.audioUrl.startsWith("blob:")) {
    try { URL.revokeObjectURL(state.audioUrl); } catch { /* ignore */ }
  }
  const url = blob ? URL.createObjectURL(blob) : null;
  state = {
    ...state,
    audioBlob: blob,
    audioUrl: url,
    audioDurationMs: durationMs,
    audioTranscript: transcript,
  };
  emit();
}

export function setStickerAudioTranscript(transcript: string) {
  state = { ...state, audioTranscript: transcript };
  emit();
}

export function setStickerField<K extends keyof StickerDraft>(key: K, value: StickerDraft[K]) {
  state = { ...state, [key]: value };
  if (key === "packName" || key === "tags" || key === "isPublic") persistText();
  emit();
}

export function setStickerSaveQueued(queued: boolean, requestId: string | null = null) {
  state = { ...state, saveQueued: queued, activeRequestId: requestId, saveError: queued ? null : state.saveError };
  emit();
}

export function setStickerSaveError(err: string | null) {
  state = { ...state, saveError: err, saveQueued: false };
  emit();
}

export function resetStickerDraft() {
  if (state.objectUrl && state.objectUrl.startsWith("blob:")) {
    try { URL.revokeObjectURL(state.objectUrl); } catch { /* ignore */ }
  }
  if (state.audioUrl && state.audioUrl.startsWith("blob:")) {
    try { URL.revokeObjectURL(state.audioUrl); } catch { /* ignore */ }
  }
  state = { ...INITIAL };
  try {
    if (typeof window !== "undefined") window.sessionStorage.removeItem(TEXT_KEY);
  } catch {
    /* ignore */
  }
  emit();
}
